// ApprovalSettings.tsx
import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  CheckCircle,
  XCircle,
  Users,
  Calendar,
  Clock,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  Eye,
  ThumbsUp,
  ThumbsDown,
  AlertCircle,
  FileText,
  RefreshCw,
  Loader2,
  Download,
  DollarSign,
  MessageSquare,
} from "lucide-react";
import { salaryApi } from "../api/salary";

// Types
interface SalaryData {
  salarytID: string;
  emptID: string;
  employeeID: string;
  name: string;
  calculationType: string;
  mode: string;
  status: string;
  approved: boolean;
  department: string;
  designation: string;
  joiningDate: string;
  SalaryDate: string;
  month: number;
  year: number;
  monthlySalary: number;
  bonus: number;
  extrapay: number;
  netSalary: number;
  approved_by?: string;
  approved_at?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  year: number;
  month: number;
  count: number;
  results: Array<{
    emptID: string;
    success: boolean;
    data: SalaryData;
  }>;
}

interface SalaryRecord extends SalaryData {
  salarytID: string;
  emptID: string;
  employeeID: string;
  name: string;
  calculationType: string;
  mode: string;
  status: string;
  approved: boolean;
  department: string;
  designation: string;
  joiningDate: string;
  SalaryDate: string;
  month: number;
  year: number;
  monthlySalary: number;
  bonus: number;
  extrapay: number;
  netSalary: number;
  approved_by?: string;
  approved_at?: string;
}

// Helper Components
const StatusBadge = ({ approved }: { approved: boolean }) => {
  if (approved) {
    return (
      <Badge className="bg-gradient-to-r from-green-100 to-green-200 text-green-700 font-semibold px-2 py-1 rounded-full">
        <CheckCircle className="w-3 h-3 mr-1" />
        Approved
      </Badge>
    );
  }
  return (
    <Badge className="bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-700 font-semibold px-2 py-1 rounded-full">
      <Clock className="w-3 h-3 mr-1" />
      Pending
    </Badge>
  );
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleDateString("en-IN");
  } catch {
    return dateString;
  }
};

// Main Component
const ApprovalSettings = () => {
  const [activeTab, setActiveTab] = useState<"pending" | "approved">("pending");
  const [allSalaryRecords, setAllSalaryRecords] = useState<SalaryRecord[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("2026");
  const [selectedMonth, setSelectedMonth] = useState<string>("03");
  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [message, setMessage] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<SalaryRecord | null>(null);
  const [approvalComments, setApprovalComments] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const years = [2024, 2025, 2026, 2027].map((year) => ({
    value: year.toString(),
    label: year.toString(),
  }));

  // Function to call export_employees API (GET)
  const callExportEmployeesAPI = useCallback(async () => {
    try {
      const response = await salaryApi.exportEmployees();
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error("Error calling export_employees:", error);
      return { success: false, error: error?.response?.data?.detail || error?.message || "Failed to export employees" };
    }
  }, []);

  // Function to call export_salary API (POST)
  const callExportSalaryAPI = useCallback(async () => {
    try {
      // Get all approved employee IDs to export
      const approvedEmployeeIds = allSalaryRecords
        .filter(record => record.approved === true)
        .map(record => record.emptID);
      
      const response = await salaryApi.exportSalary(selectedYear, selectedMonth, approvedEmployeeIds);
      return { success: true, data: response.data };
    } catch (error: any) {
      console.error("Error calling export_salary:", error);
      return { success: false, error: error?.response?.data?.detail || error?.message || "Failed to export salary" };
    }
  }, [selectedYear, selectedMonth, allSalaryRecords]);

  // Function to handle the entire export process after approval
  const handlePostApprovalExport = useCallback(async (employeeName: string) => {
    setIsExporting(true);
    setMessage("📤 Your salary is exporting...");
    setExportProgress("Starting export process...");
    
    try {
      // First API call: export_employees (GET)
      setExportProgress("Exporting employee records...");
      setMessage("📤 Exporting employee records...");
      const employeesResult = await callExportEmployeesAPI();
      
      if (!employeesResult.success) {
        throw new Error(employeesResult.error);
      }
      setExportProgress("Employee records exported successfully!");
      
      // Small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Second API call: export_salary (POST)
      setExportProgress("Exporting salary records...");
      setMessage("📤 Exporting salary records...");
      const salaryResult = await callExportSalaryAPI();
      
      if (!salaryResult.success) {
        throw new Error(salaryResult.error);
      }
      
      // Both exports successful
      setExportProgress("Export completed successfully!");
      setMessage(`✅ Successfully exported employee and salary data for ${employeeName}!`);
      
      // Check if there's a download URL in the response and trigger download
      if (salaryResult.data?.download_url) {
        window.open(salaryResult.data.download_url, '_blank');
      }
      if (employeesResult.data?.download_url) {
        window.open(employeesResult.data.download_url, '_blank');
      }
      
      return true;
    } catch (error: any) {
      console.error("Export error:", error);
      setMessage(`⚠️ Salary approved but export failed: ${error.message}`);
      setExportProgress("");
      return false;
    } finally {
      setIsExporting(false);
      setTimeout(() => {
        setMessage("");
        setExportProgress("");
      }, 5000);
    }
  }, [callExportEmployeesAPI, callExportSalaryAPI]);

  // Fetch all salary records from /salary/get_approved_salary API
  const fetchSalaryRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await salaryApi.getApprovedSalaries(selectedYear, selectedMonth);
      const apiResponse = response.data as ApiResponse;
      
      if (apiResponse.success && apiResponse.results) {
        // Extract the data from nested structure
        const formattedRecords: SalaryRecord[] = apiResponse.results.map(item => ({
          salarytID: item.data.salarytID,
          emptID: item.data.emptID,
          employeeID: item.data.employeeID,
          name: item.data.name,
          calculationType: item.data.calculationType,
          mode: item.data.mode,
          status: item.data.status,
          approved: item.data.approved,
          department: item.data.department,
          designation: item.data.designation,
          joiningDate: item.data.joiningDate,
          SalaryDate: item.data.SalaryDate,
          month: item.data.month,
          year: item.data.year,
          monthlySalary: item.data.monthlySalary,
          bonus: item.data.bonus || 0,
          extrapay: item.data.extrapay || 0,
          netSalary: item.data.netSalary,
          approved_by: item.data.approved_by,
          approved_at: item.data.approved_at,
        }));
        
        setAllSalaryRecords(formattedRecords);
      } else {
        setAllSalaryRecords([]);
      }
    } catch (error: any) {
      console.error("Error fetching salary records:", error);
      setMessage(error?.response?.data?.detail || "Failed to fetch salary records");
      setAllSalaryRecords([]);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(""), 5000);
    }
  }, [selectedYear, selectedMonth]);

  // Approve a single record with comments and then trigger exports
  const handleApproveWithComments = useCallback(async () => {
    if (!selectedRecord) return;

    setIsApproving(true);
    try {
      const employeeId = selectedRecord.emptID;
      // Call approve API with the employee ID
      await salaryApi.approveSalaries(selectedYear, selectedMonth, [employeeId]);
      
      setMessage(`✅ Successfully approved salary for ${selectedRecord.name}! Comments: ${approvalComments || "No comments"}`);
      
      // Refresh the data to get updated approved status
      await fetchSalaryRecords();
      
      // Trigger the export APIs after successful approval
      await handlePostApprovalExport(selectedRecord.name);
      
      // Reset states
      setShowApproveDialog(false);
      setSelectedRecord(null);
      setApprovalComments("");
      
      return true;
    } catch (error: any) {
      setMessage(error?.response?.data?.detail || "❌ Failed to approve salary");
      return false;
    } finally {
      setIsApproving(false);
      setTimeout(() => setMessage(""), 5000);
    }
  }, [selectedRecord, approvalComments, selectedYear, selectedMonth, fetchSalaryRecords, handlePostApprovalExport]);

  // Reject a single record with reason
  const handleRejectWithReason = useCallback(async () => {
    if (!selectedRecord) return;

    setIsApproving(true);
    try {
      // Call reject API
      await salaryApi.rejectSalaries(selectedYear, selectedMonth, [selectedRecord.emptID]);
      
      setMessage(`❌ Rejected salary for ${selectedRecord.name}! Reason: ${rejectionReason || "No reason provided"}`);
      
      // Refresh the data
      await fetchSalaryRecords();
      
      // Reset states
      setShowRejectDialog(false);
      setSelectedRecord(null);
      setRejectionReason("");
      
      return true;
    } catch (error: any) {
      setMessage(error?.response?.data?.detail || "❌ Failed to reject salary");
      return false;
    } finally {
      setIsApproving(false);
      setTimeout(() => setMessage(""), 5000);
    }
  }, [selectedRecord, rejectionReason, selectedYear, selectedMonth, fetchSalaryRecords]);

  // Export approved salaries to CSV (manual export)
  const exportApprovedSalariesToCSV = useCallback(async () => {
    const approvedRecords = allSalaryRecords.filter(record => record.approved === true);
    if (approvedRecords.length === 0) return;

    setIsExporting(true);
    setMessage("📤 Exporting approved salaries...");
    
    try {
      // Call export_salary API for all approved records
      const approvedEmployeeIds = approvedRecords.map(record => record.emptID);
      const response = await salaryApi.exportSalary(selectedYear, selectedMonth, approvedEmployeeIds);
      
      if (response.data?.download_url) {
        window.open(response.data.download_url, '_blank');
        setMessage("✅ Salary export initiated successfully!");
      } else {
        // Fallback to CSV download if no URL provided
        const headers = [
          "Employee ID",
          "Employee Name",
          "Department",
          "Designation",
          "Monthly Salary",
          "Bonus",
          "Extra Pay",
          "Net Salary",
          "Calculation Type",
          "Payment Mode",
          "Status",
          "Month",
          "Year",
          "Approved By",
          "Approved At"
        ];

        const csvData = approvedRecords.map(salary => [
          salary.employeeID,
          salary.name,
          salary.department,
          salary.designation,
          salary.monthlySalary,
          salary.bonus || 0,
          salary.extrapay || 0,
          salary.netSalary,
          salary.calculationType,
          salary.mode,
          salary.status,
          salary.month,
          salary.year,
          salary.approved_by || "Admin",
          salary.approved_at ? new Date(salary.approved_at).toLocaleString() : new Date().toLocaleString()
        ]);

        const csvContent = [
          headers.join(","),
          ...csvData.map(row => row.join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `approved_salaries_${selectedYear}_${selectedMonth}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        setMessage("✅ CSV exported successfully!");
      }
    } catch (error: any) {
      console.error("Export error:", error);
      setMessage("❌ Failed to export salaries");
    } finally {
      setIsExporting(false);
      setTimeout(() => setMessage(""), 3000);
    }
  }, [allSalaryRecords, selectedYear, selectedMonth]);

  // Get pending records (approved = false)
  const getPendingRecords = () => {
    return allSalaryRecords.filter(record => record.approved === false);
  };


  // Get approved records (approved = true)
  const getApprovedRecords = () => {
    return allSalaryRecords.filter(record => record.approved === true);
  };

  // Get filtered data based on search query and active tab
  const getFilteredData = () => {
    const data = activeTab === "pending" ? getPendingRecords() : getApprovedRecords();
    if (!searchQuery) return data;
    
    return data.filter(emp =>
      emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.employeeID?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const currentData = getFilteredData();
  const pendingCount = getPendingRecords().length;
  const approvedCount = getApprovedRecords().length;
  const totalCount = allSalaryRecords.length;

  // Open approve dialog
  const openApproveDialog = (record: SalaryRecord) => {
    setSelectedRecord(record);
    setApprovalComments("");
    setShowApproveDialog(true);
  };

  // Open reject dialog
  const openRejectDialog = (record: SalaryRecord) => {
    setSelectedRecord(record);
    setRejectionReason("");
    setShowRejectDialog(true);
  };

  const handleViewDetails = (record: SalaryRecord) => {
    setSelectedRecord(record);
    // You can implement a details dialog here if needed
  };

  const handleApproveAll = useCallback(async () => {
  const pendingRecords = getPendingRecords();

  if (pendingRecords.length === 0) {
    setMessage("⚠️ No pending records to approve");
    return;
  }

  setIsApproving(true);

  try {
    const ids = pendingRecords.map(r => r.emptID);

    await salaryApi.approveSalaries(selectedYear, selectedMonth, ids);

    setMessage(`✅ Successfully approved ${ids.length} records`);

    await fetchSalaryRecords();

    // optional export
    await handlePostApprovalExport("All Employees");

  } catch (error: any) {
    setMessage(error?.response?.data?.detail || "❌ Failed to approve all");
  } finally {
    setIsApproving(false);
    setTimeout(() => setMessage(""), 5000);
  }
}, [allSalaryRecords, selectedYear, selectedMonth]);

  // Fetch data when year or month changes
  useEffect(() => {
    fetchSalaryRecords();
  }, [selectedYear, selectedMonth, fetchSalaryRecords]);

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Payroll Approval
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Review and approve employee salary records</p>
          </div>
          {activeTab === "approved" && approvedCount > 0 && (
            <Button
              onClick={exportApprovedSalariesToCSV}
              disabled={isExporting}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-1 sm:gap-2"
            >
              {isExporting ? (
                <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
              ) : (
                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
              )}
              Export CSV
            </Button>
          )}

          {activeTab === "pending" && pendingCount > 0 && (
  <Button
    onClick={handleApproveAll}
    disabled={isApproving || isExporting}
    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center gap-2"
  >
    {isApproving ? (
      <Loader2 className="w-4 h-4 animate-spin" />
    ) : (
      <CheckCircle className="w-4 h-4" />
    )}
    Approve All ({pendingCount})
  </Button>
)}
        </div>
      </div>

      {/* Message Alert */}
      {message && (
        <div
          className={`m-4 p-3 rounded-lg shadow-md border-l-4 text-sm ${
            message.includes("✅") || message.includes("successfully")
              ? "bg-green-50 border-green-500 text-green-800"
              : message.includes("⚠️")
              ? "bg-yellow-50 border-yellow-500 text-yellow-800"
              : "bg-red-50 border-red-500 text-red-800"
          }`}
        >
          <div className="flex items-center gap-2">
            {message.includes("✅") ? (
              <span className="text-lg">✅</span>
            ) : message.includes("❌") ? (
              <span className="text-lg">❌</span>
            ) : message.includes("⚠️") ? (
              <span className="text-lg">⚠️</span>
            ) : message.includes("📤") ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : null}
            <p className="font-medium">{message}</p>
          </div>
        </div>
      )}

      {/* Export Loading Overlay */}
      {isExporting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Exporting Data</h3>
              <p className="text-sm text-gray-600 text-center">
                {exportProgress || "Your salary data is being exported. Please wait..."}
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-0 shadow-md hover:shadow-lg transition-all">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-yellow-700 mb-0.5 sm:mb-1 font-medium">Pending</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-900">{pendingCount}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-yellow-200 rounded-xl flex items-center justify-center">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-yellow-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-0 shadow-md hover:shadow-lg transition-all">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-green-700 mb-0.5 sm:mb-1 font-medium">Approved</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-900">{approvedCount}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-green-200 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-700" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-0 shadow-md hover:shadow-lg transition-all">
          <CardContent className="p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-blue-700 mb-0.5 sm:mb-1 font-medium">Total</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-900">{totalCount}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-blue-200 rounded-xl flex items-center justify-center">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white rounded-t-lg mb-4 sm:mb-6">
        <div className="flex justify-center">
          <div className="flex" role="tablist">
            <button
              onClick={() => {
                setActiveTab("pending");
                setSearchQuery("");
              }}
              className={`
                relative px-6 sm:px-12 py-3 sm:py-4 text-sm sm:text-base font-medium transition-all duration-200
                min-w-[140px] sm:min-w-[200px]
                ${activeTab === "pending"
                  ? "text-yellow-600"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }
              `}
              role="tab"
            >
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <Clock className={`w-4 h-4 sm:w-5 sm:h-5 ${activeTab === "pending" ? "text-yellow-600" : "text-gray-400"}`} />
                <div className="text-center">
                  <div className="font-semibold">Pending Approval</div>
                  <div className="text-xs text-gray-400 hidden sm:block">Awaiting review</div>
                </div>
              </div>
              {activeTab === "pending" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-600 rounded-full"></div>
              )}
            </button>

            <button
              onClick={() => {
                setActiveTab("approved");
                setSearchQuery("");
              }}
              className={`
                relative px-6 sm:px-12 py-3 sm:py-4 text-sm sm:text-base font-medium transition-all duration-200
                min-w-[140px] sm:min-w-[200px]
                ${activeTab === "approved"
                  ? "text-green-600"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }
              `}
              role="tab"
            >
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <CheckCircle className={`w-4 h-4 sm:w-5 sm:h-5 ${activeTab === "approved" ? "text-green-600" : "text-gray-400"}`} />
                <div className="text-center">
                  <div className="font-semibold">Approved</div>
                  <div className="text-xs text-gray-400 hidden sm:block">Completed approvals</div>
                </div>
              </div>
              {activeTab === "approved" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600 rounded-full"></div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <Card className="bg-white border-0 shadow-md hover:shadow-lg transition-all mb-4 sm:mb-6 overflow-hidden">
        <div
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <CardHeader className="pb-2 sm:pb-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
                <CardTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  <span className="hidden sm:inline">Filters & Controls</span>
                  <span className="sm:hidden">Filters</span>
                </CardTitle>
                <CardDescription className="hidden sm:block text-gray-500">
                  Select year and month to view salary records
                </CardDescription>
              </div>
              {isFilterOpen ? (
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              )}
            </div>
          </CardHeader>
        </div>

        {isFilterOpen && (
          <CardContent className="pt-3 sm:pt-4 animate-in slide-in-from-top-2 duration-200">
            <div className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Year</label>
                  <div className="flex items-center gap-1 sm:gap-2 bg-white border border-gray-200 rounded-lg p-1.5 sm:p-2">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="flex-1 border-0 focus:ring-0 text-xs sm:text-sm bg-transparent focus:outline-none"
                    >
                      {years.map((year) => (
                        <option key={year.value} value={year.value}>
                          {year.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Month</label>
                  <div className="flex items-center gap-1 sm:gap-2 bg-white border border-gray-200 rounded-lg p-1.5 sm:p-2">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="flex-1 border-0 focus:ring-0 text-xs sm:text-sm bg-transparent focus:outline-none"
                    >
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Search Records</label>
                <div className="relative">
                  <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 sm:w-4 sm:h-4" />
                  <input
                    type="text"
                    placeholder="Search by name or employee ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-7 sm:pl-9 pr-2 sm:pr-3 py-1.5 sm:py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Results Count */}
      <div className="mb-3 sm:mb-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          <span className="text-xs sm:text-sm text-gray-600">
            Showing <strong>{currentData.length}</strong> {activeTab === "pending" ? "pending" : "approved"} records
          </span>
        </div>
        <button
          onClick={fetchSalaryRecords}
          disabled={isLoading}
          className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Records Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full text-center py-8 sm:py-12">
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600"></div>
              <span className="text-xs sm:text-sm text-gray-500">Loading...</span>
            </div>
          </div>
        ) : currentData.length === 0 ? (
          <div className="col-span-full text-center py-8 sm:py-12">
            <div className="flex flex-col items-center gap-2">
              <Users className="w-8 h-8 sm:w-12 sm:h-12 text-gray-300" />
              <p className="text-xs sm:text-sm text-gray-500">No records found</p>
              <p className="text-[10px] sm:text-xs text-gray-400">
                {activeTab === "pending" 
                  ? "No pending salary approvals for selected period" 
                  : "No approved salary records for selected period"}
              </p>
            </div>
          </div>
        ) : (
          currentData.map((record) => (
            <Card key={record.salarytID} className="bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
              {/* Status Bar */}
              <div className={`h-1 ${record.approved ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
              
              <CardContent className="p-4">
                {/* Employee Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white text-sm font-bold">
                        {record.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm">{record.name}</h3>
                      <p className="text-xs text-gray-500">ID: {record.employeeID}</p>
                    </div>
                  </div>
                  <StatusBadge approved={record.approved} />
                </div>

                {/* Salary Details */}
                <div className="space-y-2 mb-3">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-500">Designation:</span>
                    <span className="font-medium text-gray-700">{record.designation}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-500">Monthly Salary:</span>
                    <span className="font-semibold text-blue-600">{formatCurrency(record.monthlySalary)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-500">Net Salary:</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(record.netSalary)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-500">Period:</span>
                    <span className="text-gray-600">{months.find(m => m.value === record.month?.toString().padStart(2, "0"))?.label} {record.year}</span>
                  </div>
                  {record.bonus > 0 && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500">Bonus:</span>
                      <span className="font-medium text-green-600">{formatCurrency(record.bonus)}</span>
                    </div>
                  )}
                  {record.extrapay > 0 && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500">Extra Pay:</span>
                      <span className="font-medium text-blue-600">{formatCurrency(record.extrapay)}</span>
                    </div>
                  )}
                  {/* Show approval info for approved records */}
                  {record.approved && record.approved_by && (
                    <div className="flex justify-between items-center text-xs pt-2 border-t border-gray-100">
                      <span className="text-gray-500">Approved By:</span>
                      <span className="font-medium text-green-600">{record.approved_by}</span>
                    </div>
                  )}
                  {record.approved && record.approved_at && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-gray-500">Approved At:</span>
                      <span className="font-medium text-gray-600">{formatDate(record.approved_at)}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons - Only for Pending Tab */}
                {activeTab === "pending" && !record.approved && (
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <Button
                      onClick={() => openApproveDialog(record)}
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-xs py-1.5"
                      disabled={isExporting}
                    >
                      <ThumbsUp className="w-3 h-3 mr-1" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => openRejectDialog(record)}
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 text-xs py-1.5"
                      disabled={isExporting}
                    >
                      <ThumbsDown className="w-3 h-3 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}

                {/* View Details Button for Approved Tab */}
                {activeTab === "approved" && (
                  <div className="pt-3 border-t border-gray-100">
                    <Button
                      onClick={() => handleViewDetails(record)}
                      variant="outline"
                      className="w-full text-blue-600 border-blue-200 hover:bg-blue-50 text-xs py-1.5"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View Details
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Approve Dialog with Comments */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-green-600" />
              Approve Salary - {selectedRecord?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 sm:mt-3 space-y-4">
            {/* Employee Info Summary */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Employee ID:</span>
                <span className="font-medium text-gray-900">{selectedRecord?.employeeID}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Net Salary:</span>
                <span className="font-bold text-emerald-600">{formatCurrency(selectedRecord?.netSalary || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Period:</span>
                <span className="font-medium text-gray-900">
                  {months.find(m => m.value === selectedRecord?.month?.toString().padStart(2, "0"))?.label} {selectedRecord?.year}
                </span>
              </div>
            </div>

            {/* Comments Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                Approval Comments (Optional)
              </label>
              <textarea
                value={approvalComments}
                onChange={(e) => setApprovalComments(e.target.value)}
                placeholder="Enter any comments or notes for this approval..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all text-sm resize-none"
              />
            </div>

            {/* Export Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800 flex items-center gap-2">
                <FileText className="w-3 h-3" />
                Note: After approval, employee and salary data will be automatically exported.
              </p>
            </div>

            <div className="flex gap-2 sm:gap-3 mt-4">
              <Button
                onClick={() => {
                  setShowApproveDialog(false);
                  setSelectedRecord(null);
                  setApprovalComments("");
                }}
                variant="outline"
                className="flex-1 text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApproveWithComments}
                disabled={isApproving || isExporting}
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm"
              >
                {isApproving || isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ThumbsUp className="w-4 h-4 mr-2" />
                )}
                {isApproving ? "Approving..." : isExporting ? "Exporting..." : "Confirm Approval"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog with Reason */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <ThumbsDown className="w-5 h-5 text-red-600" />
              Reject Salary - {selectedRecord?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 sm:mt-3 space-y-4">
            {/* Employee Info Summary */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Employee ID:</span>
                <span className="font-medium text-gray-900">{selectedRecord?.employeeID}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Net Salary:</span>
                <span className="font-bold text-emerald-600">{formatCurrency(selectedRecord?.netSalary || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Period:</span>
                <span className="font-medium text-gray-900">
                  {months.find(m => m.value === selectedRecord?.month?.toString().padStart(2, "0"))?.label} {selectedRecord?.year}
                </span>
              </div>
            </div>

            {/* Rejection Reason Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejecting this salary..."
                rows={3}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all text-sm resize-none"
              />
            </div>

            <div className="flex gap-2 sm:gap-3 mt-4">
              <Button
                onClick={() => {
                  setShowRejectDialog(false);
                  setSelectedRecord(null);
                  setRejectionReason("");
                }}
                variant="outline"
                className="flex-1 text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectWithReason}
                disabled={isApproving || !rejectionReason.trim()}
                className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-sm"
              >
                {isApproving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ThumbsDown className="w-4 h-4 mr-2" />
                )}
                Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalSettings;